import { observable, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n/index';
import { m } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { ImageButton } from '../button/image.button';
import { MaterialIcon } from '../icon/icons';
import { MenuComponent } from '../menu/menu';
import { type MenuItem, MenuItemComponent } from '../menu/menu.item';
import { IMithrilComponent } from '../mithril.component';
import { addStyles, DefaultTheme } from '../theme';
import { SortDirection, type SortableState } from './sortable.state';

// Re-export state types/classes so existing imports from 'sortable' still work
export { SortableState, SortDirection, type SortableStateOption, type SortablePrimitive } from './sortable.state';

export const CSS = {
	container: 'c-Sortable',
	button: 'c-Sortable__Button',
	order: {
		ascending: 'c-Sortable__Ascending',
		descending: 'c-Sortable__Descending',
		container: 'c-Sortable__Order',
	},
};

addStyles({
	[CSS.button]: {
		color: DefaultTheme.font.color.primary,
		padding: `0 ${DefaultTheme.space(1)}`,
		'font-family': DefaultTheme.font.family,
		'font-weight': 'inherit',
		'text-transform': 'inherit',
	},
	[CSS.order.ascending]: {
		'border-top': `1px solid ${DefaultTheme.font.color.divider}`,
	},
});

const SortIcon = {
	[SortDirection.ASCENDING]: MaterialIcon.KEY_UP,
	[SortDirection.DESCENDING]: MaterialIcon.KEY_DOWN,
};

export class Sortable extends IMithrilComponent {
	state: SortableState;
	menu: MenuComponent;

	@observable isOpen = false;
	constructor(state: SortableState) {
		super();
		makeObservable(this);

		this.state = state;
		this.menu = new MenuComponent({
			items: () => this.viewMenuItems(this.state),
		});
	}

	get nodes(): MithrilVNode {
		const title = Translate(T.SORT_TITLE);
		const className = `mdl-button ${CSS.button}`;
		return m.fragment({}, [
			this.menu.view(),
			m('button', { className, onclick: this.menu.show, title }, [Translate(this.state.option.title)]),
			ImageButton.view(SortIcon[this.state.direction], {
				onclick: () => {
					this.state.toggleDirection();
					this.trackSort();
				},
			}),
		]);
	}

	private trackSort() {
		const dir = this.state.direction === SortDirection.ASCENDING ? 'Ascending' : 'Descending';
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.BigMap, 'Sort', `${this.state.option.title}:${dir}`);
	}

	private viewMenuItems(sortable: SortableState): MenuItem[] {
		const items: MenuItem[] = [];

		for (const opt of sortable.options) {
			if (opt.visible != null && !opt.visible()) {
				continue;
			}

			items.push({
				selected: sortable.option.title === opt.title,
				view: MenuItemComponent.view(Translate(opt.title), opt.icon),
				action: () => {
					sortable.setSort(opt);
					this.trackSort();
				},
			});
		}

		return items;
	}
}
